import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Copy, Info } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../../utils/admin-api';
import { sessionStorage } from '../../utils/auth-api';

export function DataKeyHelper() {
  const [plcs, setPlcs] = useState<any[]>([]);
  const [dataKeys, setDataKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const session = sessionStorage.get();
  const token = session?.token || '';

  useEffect(() => {
    loadPLCsAndTags();
  }, []);

  const loadPLCsAndTags = async () => {
    try {
      setLoading(true);
      const clients = await adminApi.getClients(token);
      
      const allDataKeys: string[] = [];
      const allPlcs: any[] = [];
      
      for (const client of clients) {
        const clientPlcs = await adminApi.getPLCs(token, client.id);
        for (const plc of clientPlcs) {
          allPlcs.push({ ...plc, clientName: client.name });
          const tags = await adminApi.getTags(token, client.id, plc.id);
          tags.forEach((tag: any) => {
            allDataKeys.push(`${plc.id}_${tag.name}`);
          });
        }
      }
      
      setPlcs(allPlcs);
      setDataKeys(allDataKeys);
    } catch (error: any) {
      console.error('Error loading PLC data:', error);
      toast.error('Failed to load PLC configuration');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const copyAllKeys = () => {
    const allKeys = dataKeys.join('\n');
    navigator.clipboard.writeText(allKeys);
    toast.success(`Copied ${dataKeys.length} data keys!`);
  };

  return (
    <Card className="mt-6">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Info className="h-5 w-5" />
              Data Key Reference
            </CardTitle>
            <CardDescription>
              Use these data keys in your PDF template columns to display PLC tag values
            </CardDescription>
          </div>
          {dataKeys.length > 0 && (
            <Button variant="outline" size="sm" onClick={copyAllKeys}>
              <Copy className="h-4 w-4 mr-2" />
              Copy All
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading PLC configuration...</p>
        ) : dataKeys.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Info className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="font-medium">No PLCs or tags configured yet</p>
            <p className="text-sm mt-2">Add PLCs and tags first to see available data keys</p>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="bg-blue-50 dark:bg-blue-950/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-sm font-medium mb-2 flex items-center gap-2">
                <Info className="h-4 w-4" />
                How to use data keys:
              </p>
              <ul className="text-sm text-muted-foreground space-y-1 ml-6 list-disc">
                <li>Copy a data key below</li>
                <li>Paste it into the "Data Key" field when creating a column</li>
                <li>The format is always: <code className="bg-white dark:bg-gray-800 px-1 rounded">plc-id_tag-name</code></li>
                <li>Data keys are case-sensitive and must match exactly</li>
              </ul>
            </div>

            {plcs.map((plc) => {
              const plcKeys = dataKeys.filter(key => key.startsWith(`${plc.id}_`));
              if (plcKeys.length === 0) return null;

              return (
                <div key={plc.id} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-medium">{plc.name}</h3>
                      <p className="text-sm text-muted-foreground">
                        Client: {plc.clientName} • PLC ID: <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1 rounded">{plc.id}</code>
                      </p>
                    </div>
                    <Badge variant="secondary">{plcKeys.length} tags</Badge>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {plcKeys.map((key) => {
                      const tagName = key.replace(`${plc.id}_`, '');
                      return (
                        <div
                          key={key}
                          className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-900 rounded border hover:border-blue-400 dark:hover:border-blue-600 transition-colors group"
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-muted-foreground truncate">{tagName}</p>
                            <code className="text-xs font-mono truncate block">{key}</code>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => copyToClipboard(key)}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            <div className="bg-amber-50 dark:bg-amber-950/20 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
              <p className="text-sm font-medium mb-2">💡 Example:</p>
              <p className="text-sm text-muted-foreground mb-2">
                If you want to show the "temperature" tag from PLC "plc-001" in your report:
              </p>
              <div className="bg-white dark:bg-gray-800 p-3 rounded border font-mono text-xs">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="text-muted-foreground">Column Label:</span>
                    <div className="mt-1">Temperature</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Data Key:</span>
                    <div className="mt-1 text-blue-600 dark:text-blue-400">plc-001_temperature</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
